import { Button, Skeleton } from '@parcel/ui';
import type { Table } from '@tanstack/react-table';

export const DATA_TABLE_DEFAULT_PAGE_SIZE = 50;

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  /**
   * True while a view/filter switch is in flight. The client row model still
   * holds the previous view's rows, so its count is stale — show a placeholder
   * range instead of a misleading "of N" and freeze paging until data lands.
   */
  isLoading?: boolean;
}

export function DataTablePagination<TData>({
  table,
  isLoading = false,
}: DataTablePaginationProps<TData>) {
  const selectedCount = table.getFilteredSelectedRowModel().rows.length;
  const totalCount = table.getFilteredRowModel().rows.length;
  const { pageIndex, pageSize } = table.getState().pagination;
  const rangeStart = totalCount === 0 ? 0 : pageIndex * pageSize + 1;
  const rangeEnd = Math.min((pageIndex + 1) * pageSize, totalCount);

  return (
    <div className="flex items-center justify-between px-6 py-3 text-sm text-muted-foreground">
      <div>
        {isLoading ? (
          <Skeleton className="h-4 w-40" />
        ) : (
          <>
            {totalCount === 0 ? 'No rows.' : `Showing ${rangeStart}–${rangeEnd} of ${totalCount}`}
            {selectedCount > 0 ? ` · ${selectedCount} selected` : null}
          </>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.previousPage()}
          disabled={isLoading || !table.getCanPreviousPage()}
        >
          Previous
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => table.nextPage()}
          disabled={isLoading || !table.getCanNextPage()}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
